// Import React
import { useState, useEffect } from "react";
import { bmAPIs } from "../api-endpoints/BenchmarkingEndpoints";
import { authHeader } from "../../_helpers/auth-header";
import { AutoCloseModal } from "../../components/modal/Modal";
import { getUserList, updatePermissions } from "../permissions/PermissionsAPI";

export async function getBenchmarkCollections() {
  // PROJECT API
  const url = bmAPIs().read_benchmarks_collections_core;

  const config = {
    method: "GET",
    headers: authHeader({ authJson: true, authForm: false, guestJson: false }),
  };

  let response = await fetch(url, config);
  if (response.ok) {
    response = await response.json();
  } else {
    response = [];
  }

  return response;
}

export function useBenchmarkCollections() {
  const [loading, setLoading] = useState(true);
  const [benchmarkCollections, setBenchmarkCollections] = useState({});
  useEffect(() => {
    setLoading(true);
    getBenchmarkCollections()
      .then((res) => {
        setBenchmarkCollections(res);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  }, []);

  return {
    benchmarkCollections: {
      data: benchmarkCollections,
      loading: loading,
      set: setBenchmarkCollections,
      get: getBenchmarkCollections,
    },
  };
}

export async function saveBenchmarkCollection(
  user,
  payload,
  setErrors,
  setModal,
  benchmarkCollections,
  selectedCollection,
  setSelectedCollection
) {
  setErrors((errors) => {
    return { ...errors, saving: "Creating..." };
  });

  const url = bmAPIs().create_benchmark;

  const config = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: authHeader({
      authJson: true,
      authForm: false,
      guestJson: false,
    }),
  };

  let response = await fetch(url, config);

  if (response.ok) {
    // Create default permission
    let benchmark = await response.json();

    // Add permission for creator user
    const permissionData = {
      payload: [
        {
          userGlobalId: user.id,
          userRoleList: ["USER"],
          isRecordDeleted: false,
          companyId: null,
        },
      ],
      mappingId: benchmark.data, // This is the Benchmark ID from the create endpoint
      mappingType: "Benchmark",
      // Unused functions expected by updatePermissions
      setError: () => {},
      updateState: () => {},
    };
    await updatePermissions(permissionData);

    setErrors((errors) => {
      return { ...errors, saving: "Collection Saved" };
    });

    // Retrieve the collections after the update to refresh the UI
    const benchmarkCollectionsData = await benchmarkCollections.get();
    const newCollection = benchmarkCollectionsData.data.find(
      (c) => c.id === selectedCollection.id
    );
    // Set the selected collection again with the updated values
    await setSelectedCollection(newCollection);

    // Update the list of collections as well
    benchmarkCollections.set(benchmarkCollectionsData);
    // close modal after timeout
    AutoCloseModal(setModal);
  } else {
    response = await response.json();

    setErrors(() => {
      return { ...response.errors, saving: "Collection Create Failed" };
    });
  }
}

export function useCollectionUsers(collectionId) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState({});
  useEffect(() => {
    setLoading(true);
    getUserList(collectionId, "Benchmark", "all")
      .then((collectionUsers) => {
        setUsers(collectionUsers);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  }, [collectionId]);

  return {
    users: {
      data: users,
      loading: loading,
      get: getUserList,
      set: setUsers,
    },
  };
}

// Get Parent Collections
export function useParentCollections(childBenchmarkId) {
  const [loading, setLoading] = useState(true);
  const [parentCollections, setParentCollections] = useState([]);

  useEffect(() => {
    setLoading(true);
    getParentCollections(childBenchmarkId)
      .then((res) => {
        setParentCollections(res);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  }, [childBenchmarkId]);

  return {
    parentCollections: {
      data: parentCollections,
      loading: loading,
      set: setParentCollections,
      get: getParentCollections,
    },
  };
}

export async function getParentCollections(childBenchmarkId) {
  // PROJECT API
  const url =
    bmAPIs().read_parent_collections_core +
    "?childBenchmarkId=" +
    childBenchmarkId;

  const config = {
    method: "GET",
    headers: authHeader({ authJson: true, authForm: false, guestJson: false }),
  };

  let response = await fetch(url, config);
  if (response.ok) {
    response = await response.json();
  } else {
    response = [];
  }

  return response;
}
